<template>
  <UppyDashboard
    v-if="uppy"
    :props="uppyProps"
    :uppy="uppy"
    data-testid="uppy-dashboard"
  />
</template>

<script setup>
import ResizeObserver from 'resize-observer-polyfill';
import Uppy from '@uppy/core';
import { Dashboard as UppyDashboard } from '@uppy/vue';
import { useStore } from 'vuex';
import XHRUpload from '@uppy/xhr-upload';

import useApi from '@/shared/composables/useApi';
import useUppyUtils from '@/shared/composables/useUppyUtils';

window.ResizeObserver = ResizeObserver;

const { api } = useApi();
const store = useStore();
const { getUploadAPIEndpoint, createUppyPropsWithLocale } = useUppyUtils();

const props = defineProps({
  dense: {
    type: Boolean,
    default: false,
  },
  owner: {
    type: Object,
    default: null,
  },
  restrictions: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['uploaded']);

const uppy = ref(null);
const { uppyProps, setupUppyWithTranslations } = createUppyPropsWithLocale({
  height: props.dense ? 175 : 200,
});

onBeforeUnmount(() => {
  if (uppy.value) uppy.value.close();
});

onMounted(() => {
  setupUppyWithTranslations(uppyInit);
});

function uppyInit() {
  uppy.value = new Uppy({ restrictions: props.restrictions }).use(XHRUpload, {
    endpoint: getUploadAPIEndpoint(),
  });

  uppy.value.on('upload-success', async (file, event) => {
    const params = {
      file: {
        id: event.body.id, // remove the Shrine storage prefix
        storage: 'cache',
        metadata: {
          size: file.size,
          filename: file.name,
          mime_type: file.type,
        },
      },
      business_id: props.owner.business_id,
      category: props.owner.category,
      child_id: props.owner.child_id,
      group_id: props.owner.group_id,
      note: props.owner.note,
      owner_type: props.owner.type,
      owner_id: props.owner.id,
      tag: props.owner.tag,
      tags: props.owner.tags || [],
      description: props.owner.description,
    };

    let response;
    if (store.state.role === 'specialist') {
      response = await api.organization.attachment.create(params);
    } else if (
      store.state.role === 'manager' &&
      (props.owner.child_id || (props.owner.type === 'Employment' && !props.owner.self_upload))
    ) {
      response = await api.manager.attachment.create(params);
    } else {
      response = await api.member.attachment.create(params);
    }

    if (response?.status !== 201) return uppy.value.cancelAll();

    emit('uploaded', response.data);
    return true;
  });
}
</script>
